/**
 * Gap Filler — **rebuilt** (amendment 26). The boards this replaces are titled
 * SUPERSEDED.
 *
 * The rule the whole feature turns on, and the reason the old build was wrong:
 * **the first yes books it.** When a chair comes free ELVA offers it to several
 * patients at once, and the first one to say yes gets it — every other offer
 * stops, and anyone who says yes after is told it's gone. The old build had a
 * *held* state, so a patient who said yes was "held" while the next batch still
 * went out. There is no held state here; a booked gap is booked, and `bookGap()`
 * closes every other offer.
 *
 * Live gaps are still the most time-critical thing in AI Team, so *Working now*
 * sits above the 30-day numbers, soonest first.
 */

/** Where the empty chair came from (§1). */
export type GfSource = 'cancelled' | 'no-show' | 'short-gap' | 'never-booked' | 'added-by-staff';

export const GF_SOURCE_LABEL: Record<GfSource, string> = {
  cancelled: 'Cancelled or moved',
  'no-show': 'No-show',
  'short-gap': 'Short gap',
  'never-booked': 'Never booked',
  'added-by-staff': 'Added by staff',
};

/**
 * The funnel on every gap (§1). Labels are **eligible → offered → replied →
 * booked**; a zero box is dimmed, never hidden.
 */
export interface GfFunnel {
  eligible: number;
  offered: number;
  replied: number;
  booked: number;
}

/** A live gap's state. There is no *held* — see the file header. */
export type GfGapState = 'working' | 'waiting' | 'paused' | 'closed';

/** How a closed gap ended (§1). */
export type GfOutcome = 'filled' | 'filled-by-practice' | 'expired' | 'nobody-to-offer';

export const GF_OUTCOME_LABEL: Record<GfOutcome, string> = {
  filled: 'Filled',
  'filled-by-practice': 'Filled by the practice',
  expired: 'Expired',
  'nobody-to-offer': 'Nobody to offer',
};

/**
 * A candidate's status (§1). **No `Opened`** — a text open is not something we
 * observe, and it never meant anything to the front desk.
 */
export type GfCandidateStatus =
  | 'booked'
  | 'said-no'
  | 'no-reply'
  | 'sent'
  | 'waiting-to-send'
  | 'told-its-taken'
  | 'needs-a-person';

export const GF_STATUS_LABEL: Record<GfCandidateStatus, string> = {
  booked: 'Booked',
  'said-no': 'Said no',
  'no-reply': 'No reply',
  sent: 'Sent',
  'waiting-to-send': 'Waiting to send',
  'told-its-taken': 'Told it’s taken',
  'needs-a-person': 'Needs a person',
};

export interface GfCandidate {
  id: string;
  rank: number;
  name: string;
  /** `(210) 555-0182`, or `no mobile · email` when there is no phone. */
  contact: string;
  /** Why they are on the list, e.g. `Overdue hygiene · 8 months`. */
  why: string;
  /** Their value to the practice, e.g. `$210`. */
  value: string;
  batch: number;
  /** The last thing that happened, e.g. a quoted text or `AI call at 10:54 if no reply`. */
  lastActivity: string;
  status: GfCandidateStatus;
}

/** A bucket in *Not asked, and why* (§3). */
export interface GfExcluded {
  count: number;
  label: string;
  detail: string;
  /** *No phone or email* is fixable on the patient; the rest are not. */
  fixable?: boolean;
}

/** One of the next scheduled actions on the detail page (§3). */
export interface GfNextAction {
  at: string;
  what: string;
  detail: string;
}

/** One gap — the single record the agent card, the detail page and the store share. */
export interface GfGap {
  id: string;
  when: string; // "Today, 2:00 – 3:00 PM"
  source: GfSource;
  startsIn: string; // "starts in 3h 12m"
  provider: string; // "Dr. Patel"
  operatory: string; // "Op 3"
  minutes: number;
  fits: string; // "cleaning, exam, filling or crown"
  state: GfGapState;
  funnel: GfFunnel;
  /** The card/detail footer — the batch line, or the pause note (§2). */
  footer: string;
  /** Who paused it, for the paused footer. */
  pausedBy?: string;
  /** Set on a closed gap. */
  outcome?: GfOutcome;
  /** The batch line shown on the detail funnel card. */
  batchLine: string;
  candidates: GfCandidate[];
  /** How many candidates in total (Show all N). */
  matched: number;
  couldBeAsked: number;
  excluded: GfExcluded[];
  next: GfNextAction[];
}

/* ---- the 30-day dashboard ------------------------------------------------ */

export interface GfTodayPill {
  value: string;
  label: string;
}

export interface GfHeroSegment {
  source: GfSource;
  amount: number; // dollars, for the bar
  value: string; // "$24,800"
}

export interface GfTile {
  key: string;
  label: string;
  value: string;
  caption: string;
  delta?: string;
}

/** A week's bar (§2). No rate — bars only. */
export interface GfWeek {
  label: string;
  filled: number;
}

/** A row of *01 Where the gaps came from*. */
export interface GfSourceRow {
  source: GfSource;
  found: number;
  filled: number;
}

/** A row of *02 Channels* — which offer the patient said yes to. */
export interface GfChannel {
  label: string; // Text · AI call · Email
  booked: number;
  offers: number;
}

/** A row of *03 Recent gaps*. Every row opens its detail page. */
export interface GfRecentGap {
  id: string;
  gap: string; // "Today 9:15 AM · Dr. Patel · 60 min"
  source: GfSource;
  outcome: GfOutcome;
  /** The result line, e.g. `Rachel Moore · text · 12 min` or `8 asked, nobody said yes`. */
  result: string;
  booked: string; // "$420" or "—"
}

export interface GfAgentData {
  practiceName: string;
  titleLead: string;
  titleAccent: string;
  subtitle: string;
  today: GfTodayPill[];
  hero: {
    booked: string; // "$43,900"
    caption: string; // "74 of 112 gaps filled"
    segments: GfHeroSegment[];
    perGap: string; // "$593"
    chairTime: string; // "62 h"
  };
  tiles: GfTile[];
  weeks: GfWeek[];
  sourceRows: GfSourceRow[];
  channels: GfChannel[];
  recent: GfRecentGap[];
}
